import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { FileText, Scale, AlertTriangle, Globe, Ban, Mail } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const TermsPage = () => {
  usePageSEO();
  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fdf2f4] rounded-full mb-4">
              <FileText className="w-8 h-8 text-[#b89a6d]" />
            </div>
            <h1 className="text-4xl font-bold text-[#3a2a26] mb-4">Terms of Service</h1>
            <p className="text-gray-400">Last updated: January 31, 2026</p>
          </div>

          <div className="space-y-8">
            {/* Agreement */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Agreement to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                By accessing or using PeptidesForMedSpas.com ("the Website"), you agree to be bound 
                by these Terms of Service and all applicable laws and regulations. If you do not 
                agree with any of these terms, you are prohibited from using or accessing this site.
              </p>
            </section>

            {/* Age Requirement */}
            <section className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#b89a6d] mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 text-[#b89a6d] mr-3" />
                Age Requirement
              </h2>
              <p className="text-gray-300 leading-relaxed">
                You must be at least <strong className="text-[#3a2a26]">18 years of age</strong> to access 
                this Website. By using this Website, you represent and warrant that you are at least 
                18 years old and have the legal capacity to enter into these Terms.
              </p>
            </section>

            {/* Educational Purpose */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Educational Purpose Only</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  The content on this Website is provided for <strong className="text-[#3a2a26]">general 
                  educational and informational purposes only</strong>. It is not intended to be and 
                  should not be construed as:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Medical advice, diagnosis, or treatment</li>
                  <li>Encouragement to use any substance</li>
                  <li>Dosing, cycling, or administration guidance</li>
                  <li>A substitute for professional medical care</li>
                </ul>
                <p className="text-[#b89a6d] font-medium">
                  Always consult with a licensed healthcare professional before making any health-related decisions.
                </p>
              </div>
            </section>

            {/* Research Chemicals */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Ban className="w-6 h-6 text-[#c47267] mr-3" />
                Research Chemicals Disclaimer
              </h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Products discussed or linked on this Website are sold as <strong className="text-[#3a2a26]">
                  research chemicals for laboratory use only</strong>. By using this Website, you acknowledge:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>These products are NOT approved by the FDA for human consumption</li>
                  <li>These products are NOT intended to diagnose, treat, cure, or prevent any disease</li>
                  <li>Any form of bodily introduction is strictly prohibited by law</li>
                  <li>You will not use this information to obtain products for human or animal consumption</li>
                </ul>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Scale className="w-6 h-6 text-[#b89a6d] mr-3" />
                Intellectual Property
              </h2>
              <p className="text-gray-300 leading-relaxed">
                The content on this Website, including text, graphics, logos, images, and software, 
                is the property of PeptidesForMedSpas.com or its content suppliers and is protected 
                by copyright, trademark, and other intellectual property laws. You may not reproduce, 
                distribute, modify, or create derivative works without our prior written consent.
              </p>
            </section>

            {/* Affiliate Links */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Affiliate Disclosure</h2>
              <p className="text-gray-300 leading-relaxed">
                This Website contains affiliate links to third-party products and services. If you 
                click on an affiliate link and make a purchase, we may receive a commission at no 
                additional cost to you. These affiliate relationships do not influence the accuracy 
                of our educational content.
              </p>
            </section>

            {/* Third-Party Links */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Globe className="w-6 h-6 text-[#b89a6d] mr-3" />
                Third-Party Links
              </h2>
              <p className="text-gray-300 leading-relaxed">
                This Website may contain links to external websites. We are not responsible for the 
                content, accuracy, privacy practices, or opinions expressed on third-party sites. 
                Visiting linked websites is at your own risk. The inclusion of any link does not 
                imply endorsement by PeptidesForMedSpas.com.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Limitation of Liability</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  To the fullest extent permitted by law, PeptidesForMedSpas.com, its owners, 
                  employees, and affiliates shall not be liable for any direct, indirect, incidental, 
                  special, consequential, or punitive damages arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your use of or inability to use the Website</li>
                  <li>Any content or information obtained from the Website</li>
                  <li>Unauthorized access to your personal information</li>
                  <li>Any third-party conduct or content on the Website</li>
                </ul>
              </div>
            </section>

            {/* Indemnification */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Indemnification</h2>
              <p className="text-gray-300 leading-relaxed">
                You agree to indemnify and hold harmless PeptidesForMedSpas.com, its owners, 
                employees, and affiliates from any claims, damages, losses, or expenses arising 
                from your use of the Website, your violation of these Terms, or your violation 
                of any rights of another.
              </p>
            </section>

            {/* Governing Law */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Governing Law</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the 
                United States, without regard to its conflict of law provisions. Any disputes arising 
                under these Terms shall be subject to the exclusive jurisdiction of the courts located 
                in the United States.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Changes to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective 
                immediately upon posting to the Website. Your continued use of the Website after 
                any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Mail className="w-6 h-6 text-[#b89a6d] mr-3" />
                Contact Us
              </h2>
              <p className="text-gray-300">
                If you have questions about these Terms of Service, please contact us at:{" "}
                <a 
                  href="mailto:info@peptidesciences.ai" 
                  className="text-[#b89a6d] hover:text-amber-300 underline"
                >
                  info@peptidesciences.ai
                </a>
              </p>
            </section>

            {/* Related Links */}
            <div className="flex flex-wrap gap-4 justify-center pt-4">
              <Link 
                to="/privacy" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/disclaimer" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Disclaimer
              </Link>
              <Link 
                to="/compliance" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Compliance
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
